import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  LinearProgress,
  Tooltip,
  Badge,
  Fab,
  Skeleton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AutorenewRounded as RecycleIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as PreviewIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  ContentCopy as ContentCopyIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { useDemoMode } from '../providers/DemoModeProvider';
import { contentRecyclingApi, trackRecyclingEvent } from '../services/contentRecyclingApi';
import { getPlatformIcon, getPlatformColor } from '../utils/platformUtils';

// Mock data for demonstration
const mockRecyclingData = {
  dashboard: {
    totalOpportunities: 12,
    readyToRecycle: 8,
    totalRecycled: 24,
    averagePerformanceBoost: 35,
    topPerformingBuckets: [
      { id: 1, name: 'Product Features', opportunities: 5, avgScore: 8.2 },
      { id: 2, name: 'Customer Stories', opportunities: 3, avgScore: 7.8 },
      { id: 3, name: 'Tips & Tricks', opportunities: 4, avgScore: 7.5 }
    ],
    recentRecycling: [
      {
        id: 1,
        originalContent: 'Our new AI feature helps you save 3+ hours per week...',
        platform: 'linkedin',
        timesRecycled: 2,
        lastRecycled: '2024-01-18T10:00:00Z',
        performanceBoost: 42
      }
    ],
    upcomingOpportunities: [
      {
        id: 1,
        content: 'Behind the scenes: How we built our customer support system...',
        eligibleDate: '2024-01-25T00:00:00Z',
        score: 8.5,
        platform: 'instagram'
      }
    ]
  },
  opportunities: [
    {
      id: 1,
      originalPost: {
        id: 'post-1',
        content: 'Just launched our new AI-powered project management feature! 🚀 It automatically organizes tasks, predicts deadlines, and suggests optimal team assignments. Early users report saving 3+ hours per week on project planning. What repetitive tasks would you love to automate in your workflow?',
        platform: 'linkedin',
        createdAt: '2024-01-15T10:00:00Z',
        publishedAt: '2024-01-15T10:00:00Z'
      },
      contentBucket: {
        id: 'bucket-1',
        name: 'Product Features',
        isEvergreen: true
      },
      performanceScore: 8.7,
      peakEngagementRate: 12.5,
      totalReach: 15420,
      timesRecycled: 1,
      lastRecycledAt: '2024-01-20T14:30:00Z',
      nextEligibleDate: '2024-01-25T00:00:00Z',
      isEligible: true,
      recyclingScore: 9.2,
      suggestedVariations: [
        { type: 'platform_optimization', platform: 'twitter', confidence: 0.85 },
        { type: 'tone_shift', platform: 'instagram', confidence: 0.78 }
      ]
    },
    {
      id: 2,
      originalPost: {
        id: 'post-2',
        content: 'Customer spotlight: How @TechStartup increased their team productivity by 300% using our platform. Their secret? Automated workflows and smart task prioritization. Read their full success story in our latest case study.',
        platform: 'twitter',
        createdAt: '2024-01-10T15:00:00Z',
        publishedAt: '2024-01-10T15:00:00Z'
      },
      contentBucket: {
        id: 'bucket-2',
        name: 'Customer Stories',
        isEvergreen: true
      },
      performanceScore: 7.9,
      peakEngagementRate: 8.3,
      totalReach: 12800,
      timesRecycled: 0,
      lastRecycledAt: null,
      nextEligibleDate: '2024-01-22T00:00:00Z',
      isEligible: true,
      recyclingScore: 8.1,
      suggestedVariations: [
        { type: 'format_change', platform: 'linkedin', confidence: 0.92 },
        { type: 'visual_enhancement', platform: 'instagram', confidence: 0.67 }
      ]
    }
  ]
};

const ContentRecyclingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { isDemoMode } = useDemoMode();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [generationDialog, setGenerationDialog] = useState(false);
  const [filterDialog, setFilterDialog] = useState(false);
  const [filters, setFilters] = useState({
    bucket: 'all',
    platform: 'all',
    minScore: 7.0
  });
  const [refreshing, setRefreshing] = useState(false);

  // Load recycling data
  useEffect(() => {
    loadRecyclingData();
  }, [filters, isDemoMode]);

  useEffect(() => {
    if (user) {
      trackRecyclingEvent('dashboard_viewed', {
        userId: user.id,
        timestamp: new Date().toISOString()
      });
    }
  }, [user]);

  const loadRecyclingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // If demo mode is enabled, use mock data directly
      if (isDemoMode) {
        console.log('🎬 Demo mode enabled, using mock recycling data');
        setDashboardData(mockRecyclingData.dashboard);
        setOpportunities(mockRecyclingData.opportunities);
        setLoading(false);
        return;
      }

      // Try to load real data from API
      try {
        const [dashboardResponse, opportunitiesResponse] = await Promise.all([
          contentRecyclingApi.getDashboard(),
          contentRecyclingApi.getOpportunities(filters)
        ]);

        if (dashboardResponse.success && opportunitiesResponse.success) {
          setDashboardData(dashboardResponse.data);
          setOpportunities(opportunitiesResponse.data);
          console.log('✅ Loaded real recycling data');
        } else {
          throw new Error('Invalid API response');
        }
      } catch (apiError) {
        console.warn('⚠️ Recycling API failed, using mock data:', apiError.message);
        // Fallback to mock data
        setDashboardData(mockRecyclingData.dashboard);
        setOpportunities(mockRecyclingData.opportunities);
      }

    } catch (err) {
      console.error('Failed to load recycling data:', err);
      setError('Failed to load content recycling data');
      // Use mock data as final fallback
      setDashboardData(mockRecyclingData.dashboard);
      setOpportunities(mockRecyclingData.opportunities);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    trackRecyclingEvent('refresh_clicked', { userId: user?.id });
    await loadRecyclingData();
    setRefreshing(false);
  };

  const handleGenerateVariations = async (opportunity) => {
    setSelectedOpportunity(opportunity);
    setGenerationDialog(true);
    
    trackRecyclingEvent('generate_variations_clicked', {
      userId: user?.id,
      opportunityId: opportunity.id,
      originalPlatform: opportunity.originalPost?.platform || 'unknown'
    });
  };

  const handleApproveVariation = async (opportunity) => {
    try {
      // In real implementation, this would generate and approve variations
      await contentRecyclingApi.generateVariations(opportunity.id, {
        targetPlatforms: ['twitter', 'instagram', 'linkedin'],
        variationTypes: ['platform_optimization', 'tone_shift'],
        maxVariations: 3
      });

      trackRecyclingEvent('variation_approved', {
        userId: user?.id,
        opportunityId: opportunity.id
      });

      // Refresh data
      await loadRecyclingData();
    } catch (error) {
      console.error('Failed to generate variations:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'success';
    if (score >= 6) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="text" width={200} height={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Content Recycling
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Turn your best content into evergreen assets with AI-powered variations
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDialog(true)}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {(error || isDemoMode) && (
          <Alert severity={isDemoMode ? "info" : "warning"} sx={{ mb: 2 }}>
            {isDemoMode 
              ? "🎬 Demo Mode: Showing sample content recycling data to explore features" 
              : "Using sample data for content recycling features. Backend integration in progress."
            }
          </Alert>
        )}
      </Box>

      {/* Dashboard Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Ready to Recycle
                  </Typography>
                  <Typography variant="h4" component="div">
                    {dashboardData?.readyToRecycle || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main' }}>
                  <RecycleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Opportunities
                  </Typography>
                  <Typography variant="h4" component="div">
                    {dashboardData?.totalOpportunities || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                  <AssessmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Content Recycled
                  </Typography>
                  <Typography variant="h4" component="div">
                    {dashboardData?.totalRecycled || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.light', color: 'info.main' }}>
                  <TimelineIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Avg Performance Boost
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    +{dashboardData?.averagePerformanceBoost || 0}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recycling Opportunities */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Recycling Opportunities
          </Typography>
          <Chip 
            label={`${opportunities.length} opportunities`} 
            color="primary" 
            variant="outlined" 
          />
        </Box>

        {opportunities.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <RecycleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No recycling opportunities found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create more high-performing content to build your recycling library
            </Typography>
          </Box>
        ) : (
          <List>
            {opportunities.map((opportunity, index) => (
              <React.Fragment key={opportunity.id}>
                <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: getPlatformColor(opportunity.originalPost?.platform || 'twitter'),
                        width: 48,
                        height: 48
                      }}
                    >
                      {React.createElement(getPlatformIcon(opportunity.originalPost?.platform || 'twitter'))}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {opportunity.contentBucket?.name || 'Uncategorized Content'}
                        </Typography>
                        <Chip
                          label={`${opportunity.recyclingScore}/10`}
                          size="small"
                          color={getScoreColor(opportunity.recyclingScore)}
                        />
                        {opportunity.isEligible && (
                          <Chip label="Ready" size="small" color="success" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {opportunity.originalPost?.content?.substring(0, 150) || 'No content available'}...
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label={`${opportunity.peakEngagementRate}% engagement`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${opportunity.totalReach.toLocaleString()} reach`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Recycled ${opportunity.timesRecycled}x`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Next: ${formatDate(opportunity.nextEligibleDate)}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Generate Variations">
                        <IconButton
                          onClick={() => handleGenerateVariations(opportunity)}
                          disabled={!opportunity.isEligible}
                        >
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Quick Approve">
                        <IconButton
                          onClick={() => handleApproveVariation(opportunity)}
                          disabled={!opportunity.isEligible}
                          color="success"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                
                {index < opportunities.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Generation Dialog */}
      <Dialog open={generationDialog} onClose={() => setGenerationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Generate Content Variations
        </DialogTitle>
        <DialogContent>
          {selectedOpportunity && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Original Content:
              </Typography>
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                  {selectedOpportunity.originalPost?.content || 'No content available'}
                </Typography>
              </Paper>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Suggested Variations:
              </Typography>
              <List dense>
                {selectedOpportunity.suggestedVariations.map((variation, index) => (
                  <ListItem key={index}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: getPlatformColor(variation.platform),
                          width: 32,
                          height: 32
                        }}
                      >
                        {React.createElement(getPlatformIcon(variation.platform))}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={variation.type.replace('_', ' ').toUpperCase()}
                      secondary={`${variation.platform} • ${Math.round(variation.confidence * 100)}% confidence`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerationDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              setGenerationDialog(false);
              handleApproveVariation(selectedOpportunity);
            }}
          >
            Generate Variations
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={filterDialog} onClose={() => setFilterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Opportunities</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Content Bucket</InputLabel>
                <Select
                  value={filters.bucket}
                  onChange={(e) => setFilters(prev => ({ ...prev, bucket: e.target.value }))}
                  label="Content Bucket"
                >
                  <MenuItem value="all">All Buckets</MenuItem>
                  <MenuItem value="product">Product Features</MenuItem>
                  <MenuItem value="customer">Customer Stories</MenuItem>
                  <MenuItem value="tips">Tips & Tricks</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={filters.platform}
                  onChange={(e) => setFilters(prev => ({ ...prev, platform: e.target.value }))}
                  label="Platform"
                >
                  <MenuItem value="all">All Platforms</MenuItem>
                  <MenuItem value="twitter">Twitter</MenuItem>
                  <MenuItem value="linkedin">LinkedIn</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                  <MenuItem value="facebook">Facebook</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Minimum Score"
                type="number"
                inputProps={{ min: 0, max: 10, step: 0.1 }}
                value={filters.minScore}
                onChange={(e) => setFilters(prev => ({ ...prev, minScore: parseFloat(e.target.value) }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setFilterDialog(false);
              loadRecyclingData();
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add recycling opportunity"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => navigate('/content-buckets')}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default ContentRecyclingPage; 